import {
    useEffect,
    useState,
    useRef,
    type FC
} from "react";
import {
    type LayoutChangeEvent,
    Animated,
    Platform,
    View
} from "react-native";
import type {
    TabBarIndicatorCellType
} from "./components/indicator/type";
import type ITabBarProps from "./type";
import type {
    TabBarItemType
} from "./type";
import stylesheet, {
    TAB_BAR_MORE_KEY,
    useStyles
} from "./stylesheet";
import TabBarIndicator from "./components/indicator";
import TabBarItem from "./components/item";
import {
    useTabBarWebScroll,
    useTabBarKeyboard,
    useTabBarScroll
} from "./hook";
import {
    getTabBarSlots
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../types";
import {
    useMotionTiming
} from "../../helpers/motion";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    ActionSheetEntryType
} from "../bottomSheet/components/actionSheet/type";
import {
    Ellipsis as EllipsisIcon
} from "lucide-react-native";
import {
    getBadgeIndicatorCount
} from "../badge/components/indicator/stylesheet";
import ActionSheet from "../bottomSheet/components/actionSheet";

const TabBar: FC<ITabBarProps> = ({
    isHiddenOnKeyboard = true,
    labelBehaviour = "always",
    isHiddenOnScroll = false,
    iconAnimation = "none",
    isHidden: isHiddenProp,
    onLayout: onLayoutProp,
    customLocalize,
    customTheme,
    activeKey,
    moreTitle,
    onChange,
    items,
    style,
    title,
    ...props
}) => {
    const {
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const insets = useSafeAreaInsets();

    const enterTiming = useMotionTiming("panelEnter", customTheme);

    const exitTiming = useMotionTiming("panelExit", customTheme);

    const isKeyboardOpen = useTabBarKeyboard({
        isEnabled: isHiddenOnKeyboard
    });

    const isWebScrollHidden = useTabBarWebScroll({
        isEnabled: isHiddenOnScroll && isHiddenProp === undefined
    });

    const slide = useRef(new Animated.Value(0)).current;

    const isMorePickedRef = useRef<boolean>(false);

    const moreRef = useRef<View>(null);

    const [
        isMoreOpen,
        setIsMoreOpen
    ] = useState<boolean>(false);

    const [
        isSlideConcealed,
        setIsSlideConcealed
    ] = useState<boolean>(false);

    const [
        height,
        setHeight
    ] = useState<number>(0);

    const [
        listWidth,
        setListWidth
    ] = useState<number>(0);

    const [
        cells,
        setCells
    ] = useState<Record<string, TabBarIndicatorCellType>>({});

    const isSlideHidden = isHiddenProp ?? (isHiddenOnScroll && isWebScrollHidden);

    const {
        overflowItems,
        isMoreActive,
        visibleItems,
        currentKey
    } = getTabBarSlots({
        activeKey,
        items
    });

    const indicatorKey = isMoreActive ? TAB_BAR_MORE_KEY : currentKey ?? "";

    const isActiveDisabled = !isMoreActive && !!items.find((item) => item.key === currentKey)?.isDisabled;

    const hasOverflow = overflowItems.length > 0;

    const {
        container: containerDynamicStyle
    } = useStyles({
        isConcealed: isKeyboardOpen || isSlideConcealed,
        bottomInset: insets.bottom,
        borders,
        colors,
        spaces
    });

    useEffect(() => {
        if(!isSlideHidden) {
            setIsSlideConcealed(false);
        }

        const timing = isSlideHidden ? exitTiming : enterTiming;

        const animation = Animated.timing(slide, {
            useNativeDriver: Platform.OS !== "web",
            toValue: isSlideHidden ? 1 : 0,
            duration: timing.duration,
            easing: timing.easing,
            isInteraction: false
        });

        animation.start(({
            finished
        }) => {
            if(finished && isSlideHidden) {
                setIsSlideConcealed(true);
            }
        });

        return () => {
            animation.stop();
        };
    }, [
        isSlideHidden
    ]);

    const onLayout = (event: LayoutChangeEvent) => {
        setHeight(event.nativeEvent.layout.height);

        onLayoutProp?.(event);
    };

    const handleListLayout = (event: LayoutChangeEvent) => {
        setListWidth(event.nativeEvent.layout.width);
    };

    const handleCellLayout = (key: string, event: LayoutChangeEvent) => {
        const {
            height: cellHeight,
            width,
            x
        } = event.nativeEvent.layout;

        setCells((previous) => {
            const current = previous[key];

            if(current && current.height === cellHeight && current.width === width && current.x === x) {
                return previous;
            }

            return {
                ...previous,
                [key]: {
                    height: cellHeight,
                    width,
                    x
                }
            };
        });
    };

    const handleItemPress = (item: TabBarItemType) => {
        item.onPress?.();

        onChange?.(item.key);
    };

    const handleMoreItemPress = (item: TabBarItemType) => {
        isMorePickedRef.current = true;

        handleItemPress(item);
    };

    const handleMoreClosed = () => {
        setIsMoreOpen(false);

        const isPicked = isMorePickedRef.current;

        isMorePickedRef.current = false;

        if(Platform.OS === "web" && !isPicked) {
            moreRef.current?.focus();
        }
    };

    const renderMoreIcon: NCoreUIKitIcon = (iconProps) => {
        return <EllipsisIcon
            color={iconProps.customColor ?? colors.content.icon[iconProps.color]}
            size={iconProps.size}
        />;
    };

    const renderItem = (item: TabBarItemType) => {
        return <View
            onLayout={(event) => handleCellLayout(item.key, event)}
            style={stylesheet.cell}
            role="listitem"
            key={item.key}
        >
            <TabBarItem
                onPress={() => handleItemPress(item)}
                isActive={item.key === currentKey}
                badge={item.badge ?? item.count}
                customLocalize={customLocalize}
                labelBehaviour={labelBehaviour}
                iconAnimation={iconAnimation}
                isDisabled={item.isDisabled}
                customTheme={customTheme}
                title={item.title}
                icon={item.icon}
                href={item.href}
            />
        </View>;
    };

    const renderMore = () => {
        if(!hasOverflow) {
            return null;
        }

        const moreBadge = overflowItems.some((item) => (item.badge ?? item.count) !== undefined) ? "dot" : undefined;

        return <View
            onLayout={(event) => handleCellLayout(TAB_BAR_MORE_KEY, event)}
            style={stylesheet.cell}
            role="listitem"
        >
            <TabBarItem
                title={moreTitle ?? localize("tab-bar-more")}
                onPress={() => setIsMoreOpen(true)}
                customLocalize={customLocalize}
                labelBehaviour={labelBehaviour}
                iconAnimation={iconAnimation}
                aria-expanded={isMoreOpen}
                customTheme={customTheme}
                isActive={isMoreActive}
                aria-haspopup="dialog"
                icon={renderMoreIcon}
                isOpen={isMoreOpen}
                badge={moreBadge}
                ref={moreRef}
            />
        </View>;
    };

    const getSheetItems = (): Array<ActionSheetEntryType> => {
        return overflowItems.map((item) => {
            const badge = item.badge ?? item.count;

            return {
                meta: typeof badge === "number" && badge > 0 ? getBadgeIndicatorCount({
                    locale: activeLocale,
                    count: badge
                }) : undefined,
                onPress: () => handleMoreItemPress(item),
                isSelected: item.key === currentKey,
                isDisabled: item.isDisabled,
                role: "menuitemradio",
                label: item.title,
                icon: item.icon,
                key: item.key
            };
        });
    };

    const renderSheet = () => {
        if(!hasOverflow) {
            return null;
        }

        return <ActionSheet
            title={moreTitle ?? localize("tab-bar-more")}
            customLocalize={customLocalize}
            onClosed={handleMoreClosed}
            customTheme={customTheme}
            items={getSheetItems()}
            isActive={isMoreOpen}
        />;
    };

    return <>
        <Animated.View
            {...props}
            aria-label={title ?? localize("main-navigation")}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle,
                {
                    transform: [
                        {
                            translateY: slide.interpolate({
                                outputRange: [
                                    0,
                                    height
                                ],
                                inputRange: [
                                    0,
                                    1
                                ]
                            })
                        }
                    ]
                }
            ]}
            onLayout={onLayout}
            role="navigation"
        >
            <View
                onLayout={handleListLayout}
                style={stylesheet.list}
                role="list"
            >
                <TabBarIndicator
                    cell={isActiveDisabled ? undefined : cells[indicatorKey]}
                    labelBehaviour={labelBehaviour}
                    customTheme={customTheme}
                    listWidth={listWidth}
                />
                {visibleItems.map(renderItem)}
                {renderMore()}
            </View>
        </Animated.View>
        {renderSheet()}
    </>;
};
export default Object.assign(TabBar, {
    useScroll: useTabBarScroll
});
